import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  getAvailability,
  getMyAppointments,
  getOnlineServices,
  getOnlineStaff,
  rescheduleAppointment,
} from "../../api/clientPortalBooking";
import AuthRequiredCard from "../../components/clientPortal/AuthRequiredCard";
import { useClientPortalAuth } from "../../context/ClientPortalAuthContext";
import { parseLocalDateTimeInTimeZone } from "../../utils/datetime";
import { formatApiError } from "../../utils/formatApiError";

const stepTitles = [
  "Select Service",
  "Select Technician",
  "Select Date",
  "Select Time",
  "Confirm",
];

const formatDateLabel = (value) => {
  if (!value) return "";
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  return `${parts[0]}-${parts[1]}-${parts[2]}`;
};

const formatTimeLabel = (value) => {
  if (!value) return "";
  const parts = value.split("T");
  if (parts.length < 2) return value;
  const timePart = parts[1];
  return timePart.slice(0, 5);
};

const getServiceCategory = (service) => {
  const id = service?.category_id ?? service?.category?.id ?? null;
  const name =
    service?.category_name ??
    service?.category?.name ??
    service?.group_name ??
    service?.group ??
    "";
  if (!name && (id === null || id === undefined)) return null;
  return {
    id: String(id ?? name),
    name: String(name || "Other"),
  };
};

const defaultDateValue = () =>
  new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

export default function PortalReschedule() {
  const { salonId, settings, setAssistantHandlers } = useOutletContext();
  const { appointmentId } = useParams();
  const { token } = useClientPortalAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [appointment, setAppointment] = useState(location?.state?.appointment || null);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentError, setAppointmentError] = useState("");

  const [activeStep, setActiveStep] = useState(1);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const servicesRef = useRef([]);
  const selectedCategoryRef = useRef("all");
  const selectedServiceRef = useRef(null);

  const [selectedService, setSelectedService] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);

  const [selectedDate, setSelectedDate] = useState(defaultDateValue());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [tzName, setTzName] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [notes, setNotes] = useState("");
  const [rescheduleError, setRescheduleError] = useState("");
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [navMessage, setNavMessage] = useState("");
  const navMessageTimerRef = useRef(null);
  const [assistantNotice, setAssistantNotice] = useState("");
  const assistantNoticeTimerRef = useRef(null);
  const [highlightedServiceId, setHighlightedServiceId] = useState(null);
  const suppressResetRef = useRef({ service: false, staff: false });
  const stepRefs = {
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
    4: useRef(null),
    5: useRef(null),
  };

  const cutoffHours = useMemo(() => {
    const raw = settings?.client_cancel_modify_cutoff_hours;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return 24;
    return Math.min(168, Math.max(0, Math.round(parsed)));
  }, [settings?.client_cancel_modify_cutoff_hours]);

  const withinCutoff = useMemo(() => {
    if (!appointment?.start_time_local || !tzName) return false;
    if (!cutoffHours || cutoffHours <= 0) return false;
    const startUtc = parseLocalDateTimeInTimeZone(appointment.start_time_local, tzName);
    if (!startUtc) return false;
    const hoursUntil = (startUtc.getTime() - Date.now()) / 3600000;
    return hoursUntil < cutoffHours;
  }, [appointment?.start_time_local, cutoffHours, tzName]);

  const categories = useMemo(() => {
    const map = new Map();
    services.forEach((service) => {
      const category = getServiceCategory(service);
      if (!category) return;
      const id = String(category.id ?? category.name);
      if (!map.has(id)) {
        map.set(id, { id, name: String(category.name || "Other") });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [services]);

  const filteredServices = useMemo(() => {
    const query = serviceSearch.trim().toLowerCase();
    let list = services;
    if (selectedCategoryId !== "all") {
      list = list.filter((service) => {
        const category = getServiceCategory(service);
        return category?.id === selectedCategoryId;
      });
    }
    if (!query) return list;
    return list.filter((service) => {
      const name = service?.name || "";
      const categoryName = getServiceCategory(service)?.name || "";
      const sku = service?.sku || "";
      const haystack = `${name} ${categoryName} ${sku}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [serviceSearch, services, selectedCategoryId]);

  const selectedServiceInResults = selectedService
    ? filteredServices.some((service) => service.id === selectedService.id)
    : true;

  useEffect(() => () => {
    if (navMessageTimerRef.current) {
      clearTimeout(navMessageTimerRef.current);
    }
    if (assistantNoticeTimerRef.current) {
      clearTimeout(assistantNoticeTimerRef.current);
    }
  }, []);

  useEffect(() => {
    servicesRef.current = services;
  }, [services]);

  useEffect(() => {
    selectedCategoryRef.current = selectedCategoryId;
  }, [selectedCategoryId]);

  useEffect(() => {
    selectedServiceRef.current = selectedService;
  }, [selectedService]);

  useEffect(() => {
    if (!token || !salonId || !appointmentId) return;
    let active = true;
    const loadAppointment = async () => {
      setAppointmentLoading(true);
      setAppointmentError("");
      try {
        const data = await getMyAppointments("upcoming", token);
        if (!active) return;
        const rows = Array.isArray(data?.appointments) ? data.appointments : [];
        const match = rows.find((row) => String(row.id) === String(appointmentId));
        setAppointment(match || null);
        setTzName(data?.tz_name || "");
        if (!match) {
          setAppointmentError("Appointment not found or not eligible to reschedule.");
        }
      } catch (err) {
        if (!active) return;
        setAppointmentError(formatApiError(err));
      } finally {
        if (active) setAppointmentLoading(false);
      }
    };
    loadAppointment();
    return () => {
      active = false;
    };
  }, [appointmentId, salonId, token]);

  useEffect(() => {
    if (!appointment) return;
    if (appointment.start_time_local) {
      setSelectedDate((prev) => prev || appointment.start_time_local.split("T")[0]);
      setSelectedSlot({
        start_time_local: appointment.start_time_local,
        end_time_local: appointment.end_time_local || null,
      });
    }
    setNotes(appointment.notes || "");
  }, [appointment]);

  useEffect(() => {
    if (!token || !salonId) return;
    let active = true;
    const loadServices = async () => {
      setServicesLoading(true);
      setServicesError("");
      try {
        const data = await getOnlineServices(salonId, token);
        if (!active) return;
        setServices(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        setServicesError(formatApiError(err));
      } finally {
        if (active) setServicesLoading(false);
      }
    };
    loadServices();
    return () => {
      active = false;
    };
  }, [salonId, token]);

  useEffect(() => {
    if (categories.length < 2) {
      if (selectedCategoryId !== "all") {
        setSelectedCategoryId("all");
      }
      return;
    }
    if (
      selectedCategoryId !== "all" &&
      !categories.some((category) => category.id === selectedCategoryId)
    ) {
      setSelectedCategoryId("all");
    }
  }, [categories, selectedCategoryId]);

  useEffect(() => {
    if (!appointment || !services.length || selectedService) return;
    const match = services.find((service) => service.id === appointment.service_id);
    if (match) {
      suppressResetRef.current.service = true;
      setSelectedService(match);
    }
  }, [appointment, services, selectedService]);

  useEffect(() => {
    if (!selectedService || !token || !salonId) {
      setStaffList([]);
      return;
    }
    let active = true;
    const loadStaff = async () => {
      setStaffLoading(true);
      setStaffError("");
      try {
        const data = await getOnlineStaff(salonId, selectedService.id, token);
        if (!active) return;
        setStaffList(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        setStaffError(formatApiError(err));
      } finally {
        if (active) setStaffLoading(false);
      }
    };
    loadStaff();
    return () => {
      active = false;
    };
  }, [selectedService, salonId, token]);

  useEffect(() => {
    if (!appointment || !staffList.length || selectedStaff) return;
    const match = staffList.find((staff) => staff.id === appointment.staff_id);
    if (match) {
      suppressResetRef.current.staff = true;
      setSelectedStaff(match);
    }
  }, [appointment, staffList, selectedStaff]);

  useEffect(() => {
    if (!selectedService || !selectedStaff || !selectedDate || !token) {
      setSlots([]);
      return;
    }
    let active = true;
    const loadAvailability = async () => {
      setSlotsLoading(true);
      setSlotsError("");
      try {
        const data = await getAvailability(
          salonId,
          {
            service_id: selectedService.id,
            staff_id: selectedStaff.id,
            date: selectedDate,
            bucket_minutes: 15,
          },
          token
        );
        if (!active) return;
        let nextSlots = Array.isArray(data?.slots) ? data.slots : [];
        setTzName((prev) => data?.tz_name || prev || "");
        if (
          appointment &&
          selectedService?.id === appointment.service_id &&
          selectedStaff?.id === appointment.staff_id &&
          appointment.start_time_local
        ) {
          const existingSlot = {
            start_time_local: appointment.start_time_local,
            end_time_local: appointment.end_time_local || null,
          };
          const exists = nextSlots.some(
            (slot) => slot.start_time_local === existingSlot.start_time_local
          );
          if (!exists && appointment.start_time_local.startsWith(selectedDate)) {
            nextSlots = [existingSlot, ...nextSlots];
          }
        }
        setSlots(nextSlots);
      } catch (err) {
        if (!active) return;
        setSlotsError(formatApiError(err));
        setSlots([]);
      } finally {
        if (active) setSlotsLoading(false);
      }
    };
    loadAvailability();
    return () => {
      active = false;
    };
  }, [selectedService, selectedStaff, selectedDate, salonId, token, appointment]);

  useEffect(() => {
    if (!selectedService) return;
    if (suppressResetRef.current.service) {
      suppressResetRef.current.service = false;
      return;
    }
    setSelectedStaff(null);
    setSelectedSlot(null);
    setActiveStep(2);
  }, [selectedService]);

  useEffect(() => {
    if (!selectedStaff) return;
    if (suppressResetRef.current.staff) {
      suppressResetRef.current.staff = false;
      return;
    }
    setSelectedSlot(null);
    setActiveStep(3);
  }, [selectedStaff]);

  useEffect(() => {
    if (!selectedDate) return;
    if (selectedService && selectedStaff) {
      setActiveStep(4);
    }
  }, [selectedDate, selectedService, selectedStaff]);

  useEffect(() => {
    if (!selectedSlot) return;
    setActiveStep(5);
  }, [selectedSlot]);

  const setNavMessageWithTimeout = (message) => {
    setNavMessage(message);
    if (navMessageTimerRef.current) {
      clearTimeout(navMessageTimerRef.current);
    }
    navMessageTimerRef.current = setTimeout(() => {
      setNavMessage("");
    }, 3000);
  };

  const setAssistantNoticeWithTimeout = (message) => {
    setAssistantNotice(message);
    if (assistantNoticeTimerRef.current) {
      clearTimeout(assistantNoticeTimerRef.current);
    }
    assistantNoticeTimerRef.current = setTimeout(() => {
      setAssistantNotice("");
    }, 3000);
  };

  const getStepGate = (step) => {
    if (step <= 1) return { ok: true };
    if (!selectedService) {
      return { ok: false, message: "Please select a service first." };
    }
    if (step === 2) return { ok: true };
    if (!selectedStaff) {
      return { ok: false, message: "Please select a technician first." };
    }
    if (step === 3) return { ok: true };
    if (!selectedDate) {
      return { ok: false, message: "Please select a date first." };
    }
    if (step === 4) return { ok: true };
    if (!selectedSlot) {
      return { ok: false, message: "Please select a time first." };
    }
    return { ok: true };
  };

  const scrollToStep = useCallback((step) => {
    const target = stepRefs?.[step]?.current;
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const focusServiceCard = useCallback((serviceId) => {
    const card = document.querySelector(`[data-service-card="${serviceId}"]`);
    if (!card) return;
    card.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedServiceId(serviceId);
    setTimeout(() => setHighlightedServiceId(null), 1600);
  }, []);

  const selectServiceFromAssistant = useCallback((serviceId, serviceName) => {
    const match = servicesRef.current.find(
      (service) => service.id === serviceId
    );
    if (!match) {
      setAssistantNoticeWithTimeout(
        "That service is not available online right now."
      );
      return;
    }
    const category = getServiceCategory(match);
    setSelectedCategoryId(category?.id || "all");
    setServiceSearch("");
    setSelectedService(match);
    setActiveStep(2);
    scrollToStep(2);
    setAssistantNoticeWithTimeout(`Selected: ${serviceName || match.name}`);
  }, [scrollToStep]);

  const viewServiceFromAssistant = useCallback((serviceId) => {
    const match = servicesRef.current.find(
      (service) => service.id === serviceId
    );
    if (match) {
      const category = getServiceCategory(match);
      setSelectedCategoryId(category?.id || "all");
      setServiceSearch("");
    }
    setActiveStep(1);
    scrollToStep(1);
    setTimeout(() => focusServiceCard(serviceId), 350);
  }, [focusServiceCard, scrollToStep]);

  const getAssistantContext = useCallback(() => {
    return {
      selected_category_id: selectedCategoryRef.current,
      selected_service_id: selectedServiceRef.current?.id ?? null,
    };
  }, []);

  useEffect(() => {
    if (typeof setAssistantHandlers !== "function") return;
    setAssistantHandlers({
      selectService: selectServiceFromAssistant,
      viewService: viewServiceFromAssistant,
      getContext: getAssistantContext,
    });
    return () => setAssistantHandlers(null);
  }, [
    setAssistantHandlers,
    selectServiceFromAssistant,
    viewServiceFromAssistant,
    getAssistantContext,
  ]);

  const handleStepPillClick = (step) => {
    const gate = getStepGate(step);
    if (!gate.ok) {
      setNavMessageWithTimeout(gate.message);
      return;
    }
    setNavMessage("");
    setActiveStep(step);
    scrollToStep(step);
  };

  const handleSave = async () => {
    if (!selectedService || !selectedStaff || !selectedSlot || !token) return;
    if (withinCutoff) {
      setRescheduleError(
        `This appointment can't be changed within ${cutoffHours} hours of the start time. Please contact the salon.`
      );
      return;
    }
    setRescheduleSaving(true);
    setRescheduleError("");
    try {
      const payload = {
        new_service_id: selectedService.id,
        new_staff_id: selectedStaff.id,
        new_start_at: selectedSlot.start_time_local,
        notes: notes.trim() || null,
      };
      await rescheduleAppointment(appointmentId, payload, token);
      navigate(`/portal/salon/${salonId}/appointments`, {
        state: { successMessage: "Appointment rescheduled." },
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "APPOINTMENT_CONFLICT") {
        setRescheduleError("That time was just booked. Please pick another slot.");
      } else if (detail === "STAFF_TIME_OFF") {
        setRescheduleError("That time is no longer available. Please pick another slot.");
      } else {
        setRescheduleError(formatApiError(err));
      }
    } finally {
      setRescheduleSaving(false);
    }
  };

  const serviceSummary = selectedService
    ? `${selectedService.name} (${selectedService.duration_minutes || "?"} mins)`
    : "Not selected";
  const staffSummary = selectedStaff ? selectedStaff.display_name : "Not selected";
  const dateSummary = selectedDate ? formatDateLabel(selectedDate) : "Not selected";
  const timeSummary = selectedSlot
    ? `${formatTimeLabel(selectedSlot.start_time_local)} - ${formatTimeLabel(
        selectedSlot.end_time_local || selectedSlot.start_time_local
      )}`
    : "Not selected";

  const showLoginPrompt = !token;

  if (showLoginPrompt) {
    return (
      <AuthRequiredCard
        title="Sign in to reschedule"
        subtitle="Please log in to update your appointment."
        salonId={salonId}
      />
    );
  }

  if (appointmentLoading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="text-sm text-slate-500">Loading appointment...</div>
      </div>
    );
  }

  if (appointmentError) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
        {appointmentError}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
              Reschedule appointment
            </h2>
            <p className="mt-1 text-sm md:text-base text-slate-600">
              Update the details below to reschedule your visit.
            </p>
          </div>
          {tzName && (
            <span className="rounded-full border border-slate-200 px-4 py-2 text-xs md:text-sm font-semibold text-slate-600">
              Times shown in {tzName}
            </span>
          )}
        </div>

        {withinCutoff && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            This appointment can't be changed within {cutoffHours} hours of the start time.
            Please contact the salon.
          </div>
        )}

        <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-2 text-xs md:text-sm">
          {stepTitles.map((title, index) => {
            const stepNumber = index + 1;
            const isActive = activeStep === stepNumber;
            const isComplete = activeStep > stepNumber;
            const gate = getStepGate(stepNumber);
            const isBlocked = !gate.ok;
            return (
              <button
                key={title}
                type="button"
                onClick={() => handleStepPillClick(stepNumber)}
                aria-current={isActive ? "step" : undefined}
                aria-disabled={isBlocked}
                className={`rounded-full border px-3 py-2 text-center font-semibold transition ${
                  isActive
                    ? "border-rose-300 bg-rose-50 text-rose-700"
                    : isComplete
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 text-slate-500"
                } ${isBlocked ? "opacity-50 cursor-not-allowed" : "hover:border-slate-300"}`}
              >
                Step {stepNumber}: {title}
              </button>
            );
          })}
        </div>
        {navMessage && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs md:text-sm text-slate-600">
            {navMessage}
          </div>
        )}
        {assistantNotice && (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs md:text-sm text-emerald-700">
            {assistantNotice}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div id="step-1" ref={stepRefs[1]} className="rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 1</h3>
              <p className="text-sm text-slate-600">Choose a service.</p>
            </div>
            <span className="text-xs text-slate-500">{serviceSummary}</span>
          </div>

          {servicesError && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {servicesError}
            </div>
          )}
          {servicesLoading ? (
            <div className="mt-4 text-sm text-slate-500">Loading services...</div>
          ) : (
            <>
              {categories.length >= 2 && (
                <div className="mt-4">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Category
                  </label>
                  <div className="relative mt-2">
                    <select
                      value={selectedCategoryId}
                      onChange={(event) => setSelectedCategoryId(event.target.value)}
                      className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    >
                      <option value="all">All categories</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="mt-4">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Search services
                </label>
                <div className="relative mt-2">
                  <input
                    type="text"
                    value={serviceSearch}
                    onChange={(event) => setServiceSearch(event.target.value)}
                    placeholder="Search by service name..."
                    className="h-11 w-full rounded-xl border border-slate-300 px-4 pr-10 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                  {serviceSearch.trim() && (
                    <button
                      type="button"
                      onClick={() => setServiceSearch("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-slate-600"
                      aria-label="Clear search"
                    >
                      X
                    </button>
                  )}
                </div>
                {selectedService && !selectedServiceInResults && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span>
                      Selected: {selectedService.name} (not in results)
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedService(null)}
                      className="text-rose-600 hover:text-rose-700 hover:underline"
                    >
                      Clear selection
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredServices.map((service) => {
                  const isSelected = selectedService?.id === service.id;
                  const isHighlighted = highlightedServiceId === service.id;
                  return (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => setSelectedService(service)}
                      data-service-card={service.id}
                      className={`rounded-2xl border p-4 text-left transition ${
                        isSelected
                          ? "border-rose-300 bg-rose-50"
                          : "border-slate-200 hover:border-slate-300"
                      } ${isHighlighted ? "ring-2 ring-rose-300 animate-pulse" : ""}`}
                    >
                      <div className="text-base font-semibold text-slate-900">
                        {service.name}
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        {service.duration_minutes} mins
                      </div>
                      {service.description && (
                        <p className="mt-2 text-xs text-slate-500">
                          {service.description}
                        </p>
                      )}
                    </button>
                  );
                })}
                {!filteredServices.length && !servicesLoading && services.length > 0 && (
                  <div className="text-sm text-slate-500">
                    No services match your search.
                  </div>
                )}
                {!services.length && !servicesLoading && (
                  <div className="text-sm text-slate-500">
                    No online services are available yet.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div
          id="step-2"
          ref={stepRefs[2]}
          className={`rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28 ${
            selectedService ? "" : "opacity-60"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 2</h3>
              <p className="text-sm text-slate-600">Choose a technician.</p>
            </div>
            <span className="text-xs text-slate-500">{staffSummary}</span>
          </div>

          {staffError && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {staffError}
            </div>
          )}
          {staffLoading ? (
            <div className="mt-4 text-sm text-slate-500">Loading technicians...</div>
          ) : (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {staffList.map((staff) => {
                const isSelected = selectedStaff?.id === staff.id;
                return (
                  <button
                    key={staff.id}
                    type="button"
                    onClick={() => selectedService && setSelectedStaff(staff)}
                    className={`rounded-2xl border p-4 text-left transition ${
                      isSelected
                        ? "border-rose-300 bg-rose-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    disabled={!selectedService}
                  >
                    <div className="text-base font-semibold text-slate-900">
                      {staff.display_name}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">Available online</div>
                  </button>
                );
              })}
              {!staffList.length && selectedService && !staffLoading && (
                <div className="text-sm text-slate-500">
                  No technicians are available for this service.
                </div>
              )}
            </div>
          )}
        </div>

        <div
          id="step-3"
          ref={stepRefs[3]}
          className={`rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28 ${
            selectedStaff ? "" : "opacity-60"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 3</h3>
              <p className="text-sm text-slate-600">Choose a date.</p>
            </div>
            <span className="text-xs text-slate-500">{dateSummary}</span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              disabled={!selectedStaff}
              className="h-11 rounded-xl border border-slate-300 px-4 text-sm"
            />
            <span className="text-xs text-slate-500">
              Pick a day that works for you.
            </span>
          </div>
        </div>

        <div
          id="step-4"
          ref={stepRefs[4]}
          className={`rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28 ${
            selectedStaff && selectedDate ? "" : "opacity-60"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 4</h3>
              <p className="text-sm text-slate-600">Choose a time slot.</p>
            </div>
            <span className="text-xs text-slate-500">{timeSummary}</span>
          </div>

          {slotsError && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {slotsError}
            </div>
          )}
          {slotsLoading ? (
            <div className="mt-4 text-sm text-slate-500">Loading times...</div>
          ) : (
            <div className="mt-4 flex flex-wrap gap-3">
              {slots.map((slot) => {
                const isSelected =
                  selectedSlot?.start_time_local === slot.start_time_local;
                return (
                  <button
                    key={slot.start_time_local}
                    type="button"
                    onClick={() => setSelectedSlot(slot)}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                      isSelected
                        ? "border-rose-300 bg-rose-50 text-rose-700"
                        : "border-slate-200 text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    {formatTimeLabel(slot.start_time_local)}
                  </button>
                );
              })}
              {!slots.length && !slotsLoading && (
                <div className="text-sm text-slate-500">
                  No availability for this date. Please choose another day.
                </div>
              )}
            </div>
          )}
        </div>

        <div
          id="step-5"
          ref={stepRefs[5]}
          className={`rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28 ${
            selectedSlot ? "" : "opacity-60"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 5</h3>
              <p className="text-sm text-slate-600">Review and confirm.</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">Service</div>
              <div className="font-semibold text-slate-900">{serviceSummary}</div>
            </div>
            <div className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">Technician</div>
              <div className="font-semibold text-slate-900">{staffSummary}</div>
            </div>
            <div className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">Date</div>
              <div className="font-semibold text-slate-900">{dateSummary}</div>
            </div>
            <div className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">Time</div>
              <div className="font-semibold text-slate-900">{timeSummary}</div>
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm font-semibold text-slate-600">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm"
              placeholder="Let us know anything helpful before your visit."
              disabled={!selectedSlot}
            />
          </div>

          {rescheduleError && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {rescheduleError}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={!selectedSlot || rescheduleSaving || withinCutoff}
              className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {rescheduleSaving ? "Saving..." : "Save changes"}
            </button>
            <Link
              to={`/portal/salon/${salonId}/appointments`}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
